import { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  LabelList,
  Legend
} from 'recharts';

import * as actions from '../../actions/reports';
import { formatMoney } from '../../utils';
import { verifyAdmin } from '../../utils/verify-admin';
import Loading from '../../components/Loading';

function brDate(d) {
  return moment(d, 'YYYY-MM-DD').format('DD/MM');
}

function preparePaymentMix(items, topN = 7, minPct = 0.03) {
  if (!items.length) return [];

  const sorted = [...items].sort((a, b) => b.totalAmount - a.totalAmount);
  const total = sorted.reduce((sum, item) => sum + item.totalAmount, 0);
  if (total <= 0) return [];

  const main = [];
  const others = [];

  sorted.forEach((item) => {
    const pct = item.totalAmount / total;
    if (main.length < topN && pct >= minPct) {
      main.push({ ...item, pct });
    } else {
      others.push(item);
    }
  });

  if (others.length > 0) {
    const othersAmount = others.reduce((sum, item) => sum + item.totalAmount, 0);
    const othersCount = others.reduce((sum, item) => sum + item.count, 0);
    main.push({
      label: `Outros (${others.length})`,
      totalAmount: othersAmount,
      count: othersCount,
      pct: othersAmount / total
    });
  }

  return main.map((item) => ({
    ...item,
    pctLabel: `${(item.pct * 100).toFixed(1)}%`
  }));
}

function Dashboard({ admin, dashboard, getDashboard }) {
  const [initial, setInitial] = useState(() => moment().utcOffset(-3).startOf('month').format('YYYY-MM-DD'));
  const [final, setFinal] = useState(() => moment().utcOffset(-3).format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!admin || !admin.loja || !verifyAdmin(admin, 'owner')) return;
    setLoading(true);
    getDashboard({ initial, final }, admin.loja, () => setLoading(false));
  }, [admin, initial, final, getDashboard]);

  useEffect(() => {
    if (admin && admin.loja && verifyAdmin(admin, 'owner')) {
      load();
    }
  }, [admin, load]);

  function setRangeToday() {
    const z = moment().utcOffset(-3);
    setInitial(z.format('YYYY-MM-DD'));
    setFinal(z.format('YYYY-MM-DD'));
  }

  function setRangeWeek() {
    const z = moment().utcOffset(-3);
    setInitial(z.clone().startOf('isoWeek').format('YYYY-MM-DD'));
    setFinal(z.format('YYYY-MM-DD'));
  }

  function setRangeMonth() {
    const z = moment().utcOffset(-3);
    setInitial(z.clone().startOf('month').format('YYYY-MM-DD'));
    setFinal(z.format('YYYY-MM-DD'));
  }

  const ov = dashboard && dashboard.overview;
  const prev = dashboard && dashboard.previousPeriod;
  const growth = dashboard && dashboard.revenueGrowthPct;
  const series = (dashboard && dashboard.timeSeries) || [];
  const mix = preparePaymentMix((dashboard && dashboard.paymentMix) || []);
  const products = dashboard && dashboard.products;
  const upsell = dashboard && dashboard.upsell;
  const customers = dashboard && dashboard.customers;
  const presets = dashboard && dashboard.presets;
  const prevRange = dashboard && dashboard.previousPeriodRange;

  const revenueCompare =
    dashboard && ov && prev ? [{ name: 'Receita', atual: ov.revenue, anterior: prev.revenue }] : [];
  const ordersCompare =
    dashboard && ov && prev
      ? [{ name: 'Pedidos pagos', atual: ov.paidOrdersCount, anterior: prev.paidOrdersCount }]
      : [];

  const topRev = (products && products.topByRevenue) || [];
  const topQty = (products && products.topByQty) || [];

  if (!admin || !verifyAdmin(admin, 'owner')) {
    return null;
  }

  return (
    <div className="Dashboard container-centered">
      <div className="wrapper">
        <h2 className="page-title">
          <i className="fas fa-chart-line" aria-hidden="true" />
          Painel (KPIs)
        </h2>
        <p style={{ color: '#7a7b7d', fontSize: 15, marginBottom: 8 }}>
          Receita e pedidos pagos consideram apenas <strong>Pagamento Aprovado</strong> e excluem pedidos cancelados,
          como no relatório financeiro.
        </p>

        <div className="dashboard-toolbar">
          <div className="input-search">
            <label htmlFor="dash-start">Início</label>
            <input
              id="dash-start"
              type="date"
              value={initial}
              onChange={(e) => setInitial(e.target.value)}
            />
          </div>
          <div className="input-search">
            <label htmlFor="dash-end">Fim</label>
            <input
              id="dash-end"
              type="date"
              value={final}
              onChange={(e) => setFinal(e.target.value)}
            />
          </div>
          <button type="button" className="primary" onClick={load}>
            Atualizar
          </button>
          <div className="quick-range">
            <span>Atalhos</span>
            <button type="button" onClick={setRangeToday}>
              Hoje
            </button>
            <button type="button" onClick={setRangeWeek}>
              Semana
            </button>
            <button type="button" onClick={setRangeMonth}>
              Mês
            </button>
          </div>
        </div>

        {loading && <Loading />}

        {!loading && dashboard && ov && (
          <>
            {presets && presets.today && presets.week && presets.month && (
              <>
                <h3 className="section-title">Referência rápida (calendário)</h3>
                <div className="kpi-grid" style={{ marginBottom: 8 }}>
                  <div className="kpi-card accent">
                    <h4>Hoje</h4>
                    <div className="value">{formatMoney(presets.today.revenue)}</div>
                    <div className="hint">
                      {presets.today.paidOrdersCount} pedidos pagos · ticket {formatMoney(presets.today.avgTicket)}
                    </div>
                  </div>
                  <div className="kpi-card accent">
                    <h4>Semana (seg → hoje)</h4>
                    <div className="value">{formatMoney(presets.week.revenue)}</div>
                    <div className="hint">
                      {presets.week.paidOrdersCount} pedidos pagos · ticket {formatMoney(presets.week.avgTicket)}
                    </div>
                  </div>
                  <div className="kpi-card accent">
                    <h4>Mês (até hoje)</h4>
                    <div className="value">{formatMoney(presets.month.revenue)}</div>
                    <div className="hint">
                      {presets.month.paidOrdersCount} pedidos pagos · ticket {formatMoney(presets.month.avgTicket)}
                    </div>
                  </div>
                </div>
              </>
            )}

            <h3 className="section-title">Visão geral — período selecionado</h3>
            <div className="kpi-grid">
              <div className="kpi-card">
                <h4>Receita</h4>
                <div className="value">{formatMoney(ov.revenue)}</div>
                <div className="hint">
                  {growth === null ? (
                    'Sem período anterior comparável'
                  ) : (
                    <span className={growth >= 0 ? 'growth-positive' : 'growth-negative'}>
                      {growth >= 0 ? '+' : ''}
                      {growth}% vs período anterior
                    </span>
                  )}
                  {prevRange && (
                    <span>
                      {' '}
                      ({brDate(prevRange.initial)}–{brDate(prevRange.final)})
                    </span>
                  )}
                </div>
              </div>
              <div className="kpi-card">
                <h4>Pedidos pagos</h4>
                <div className="value">{ov.paidOrdersCount}</div>
                <div className="hint">Ticket médio {formatMoney(ov.avgTicket)}</div>
              </div>
              <div className="kpi-card">
                <h4>Taxa de cancelamento</h4>
                <div className="value">{ov.cancellationRate}%</div>
                <div className="hint">
                  {ov.cancelledOrdersInPeriod} cancelados / {ov.totalOrdersInPeriod} pedidos criados no período
                </div>
              </div>
            </div>

            <h3 className="section-title">Vendas e financeiro</h3>
            <div className="chart-panel">
              <h3>Receita por dia (aprovados)</h3>
              {series.length === 0 ? (
                <div className="empty-chart">Nenhum pagamento aprovado neste intervalo.</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#dfe0e2" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={brDate} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${v}`} />
                    <Tooltip
                      formatter={(value) => [formatMoney(value), 'Receita']}
                      labelFormatter={(l) => (l ? moment(l, 'YYYY-MM-DD').format('DD/MM/YYYY') : '')}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="#75a500" fill="#89c600" fillOpacity={0.35} name="Receita" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="chart-panel payment-mix-panel">
              <h3>Mix por forma de pagamento</h3>
              {mix.length === 0 ? (
                <div className="empty-chart">Sem dados no período.</div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, mix.length * 36 + 24)}>
                  <BarChart
                    layout="vertical"
                    data={mix}
                    margin={{ top: 4, right: 56, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#dfe0e2" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${v}`} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={148}
                      tick={{ fontSize: 12, fill: '#495057' }}
                    />
                    <Tooltip
                      formatter={(value, _name, props) => [
                        `${formatMoney(value)} (${props.payload.pctLabel}) · ${props.payload.count} pedidos`,
                        'Receita'
                      ]}
                      labelFormatter={(label) => label}
                    />
                    <Bar dataKey="totalAmount" name="Receita" fill="#89c600" radius={[0, 4, 4, 0]} maxBarSize={22}>
                      <LabelList dataKey="pctLabel" position="right" style={{ fontSize: 12, fill: '#495057', fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="two-cols" style={{ marginTop: 16 }}>
              <div className="chart-panel compare-bars">
                <h3>Receita — período vs anterior</h3>
                {revenueCompare.length === 0 ? (
                  <div className="empty-chart">Sem dados.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={revenueCompare} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#dfe0e2" />
                      <XAxis dataKey="name" hide />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${v}`} />
                      <Tooltip
                        formatter={(value, name) =>
                          name === 'atual' || name === 'anterior' ? formatMoney(value) : value
                        }
                      />
                      <Legend />
                      <Bar dataKey="atual" name="Período selecionado" fill="#89c600" />
                      <Bar dataKey="anterior" name="Período anterior" fill="#90a1e3" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="chart-panel compare-bars">
                <h3>Pedidos pagos — período vs anterior</h3>
                {ordersCompare.length === 0 ? (
                  <div className="empty-chart">Sem dados.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={ordersCompare} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#dfe0e2" />
                      <XAxis dataKey="name" hide />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="atual" name="Período selecionado" fill="#89c600" />
                      <Bar dataKey="anterior" name="Período anterior" fill="#90a1e3" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <p style={{ fontSize: 13, color: '#7a7b7d', marginTop: 8 }}>
              Ticket médio no período anterior: {prev ? formatMoney(prev.avgTicket) : '—'} · atual:{' '}
              {formatMoney(ov.avgTicket)}
            </p>

            <h3 className="section-title">Produtos e mix</h3>
            {products && products.concentration && (
              <div className="two-cols">
                <div className="data-table-wrap">
                  <h3>Top por receita (linhas do carrinho, pedidos pagos)</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Qtd</th>
                        <th>Receita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRev.map((row) => (
                        <tr key={row.title}>
                          <td>{row.title}</td>
                          <td>{row.qty}</td>
                          <td>{formatMoney(row.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ marginTop: 12, fontSize: 13, color: '#7a7b7d' }}>
                    Concentração: top {products.concentration.topN} SKUs respondem por{' '}
                    <strong>{products.concentration.sharePct}%</strong> da receita somada das linhas de produto no período.
                  </p>
                </div>
                <div className="data-table-wrap">
                  <h3>Top por quantidade</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Qtd</th>
                        <th>Receita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topQty.map((row) => (
                        <tr key={`q-${row.title}`}>
                          <td>{row.title}</td>
                          <td>{row.qty}</td>
                          <td>{formatMoney(row.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {upsell && (
              <div className="kpi-grid" style={{ marginTop: 16 }}>
                <div className="kpi-card">
                  <h4>Upsell (itens com upsell no carrinho)</h4>
                  <div className="value">{upsell.upsellConversionPct}%</div>
                  <div className="hint">
                    {upsell.paidOrdersWithUpsell} pedidos com upsell · receita em upsells{' '}
                    {formatMoney(upsell.upsellRevenue)}
                  </div>
                </div>
              </div>
            )}

            {customers && customers.newVsReturning && customers.ordersPerCustomer && (
              <>
                <h3 className="section-title">Clientes (pedidos pagos no período)</h3>
                <div className="kpi-grid">
                  <div className="kpi-card">
                    <h4>Novos vs recorrentes</h4>
                    <div className="value">
                      {customers.newVsReturning.new} / {customers.newVsReturning.returning}
                    </div>
                    <div className="hint">
                      Novos (sem pagamento aprovado prévio à data inicial) vs já compraram antes
                    </div>
                  </div>
                  <div className="kpi-card">
                    <h4>Pedidos por cliente</h4>
                    <div className="value">{customers.ordersPerCustomer.avg}</div>
                    <div className="hint">
                      Média entre {customers.ordersPerCustomer.customersWithPaidOrders} clientes · máximo{' '}
                      {customers.ordersPerCustomer.max} pedidos
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  admin: state.auth.usuario,
  dashboard: state.reports.dashboard
});

export default connect(mapStateToProps, actions)(Dashboard);
